import {
  type ImageType,
  type MonetaryUnit,
  type Perforation,
  type PerforationType,
  type SecurityPerforation,
  supportsSecurityPerforation,
  validateSecurityPerforation,
} from "@briefmarken_katalog/types";

import type { TranslationKey } from "$lib/i18n";
import { EditorModelBase } from "./EditorModelBase.svelte";

export class StampBaseModel extends EditorModelBase {
  title = $state("");
  description = $state("");

  face_value_value = $state<number>(0);
  face_value_unit = $state<MonetaryUnit | undefined>(undefined);

  picture = $state<ImageType | undefined>(undefined);
  pictureDirty = $state(false);

  perforation_type = $state<PerforationType | undefined>(undefined);
  perforation_count_horizontal = $state<number>(0);
  perforation_count_vertical = $state<number>(0);
  /** Only carried by line and comb perforations; undefined means "no security holes". */
  security_perforation = $state<SecurityPerforation | undefined>(undefined);

  isReadonly = $state(false);

  isTitleInvalid = $derived(this.title === "");
  isFaceValueAmountInvalid = $derived(this.face_value_value === 0 || this.face_value_value === undefined);
  isFaceValueUnitInvalid = $derived(!this.face_value_unit);

  /** The three perforation fields plus the security holes — loaded, snapshot and reset as one unit. */
  protected loadPerforation(perforation: Perforation | undefined): void {
    this.perforation_type = perforation?.type;
    this.perforation_count_horizontal = perforation?.horizontal ?? 0;
    this.perforation_count_vertical = perforation?.vertical ?? 0;
    this.security_perforation = perforation?.security;
  }

  protected perforationSnapshot() {
    return {
      perforation_type: this.perforation_type,
      perforation_count_horizontal: this.perforation_count_horizontal,
      perforation_count_vertical: this.perforation_count_vertical,
      security_perforation: this.security_perforation,
    };
  }

  /** A security perforation only survives on a type that has a hole row to put it in. */
  protected buildPerforation(): Perforation | undefined {
    if (!this.perforation_type) return undefined;
    return {
      type: this.perforation_type,
      horizontal: this.perforation_count_horizontal,
      vertical: this.perforation_count_vertical,
      security: supportsSecurityPerforation(this.perforation_type) ? this.security_perforation : undefined,
    };
  }

  protected resetPerforation(): void {
    this.perforation_type = undefined;
    this.perforation_count_horizontal = 0;
    this.perforation_count_vertical = 0;
    this.security_perforation = undefined;
  }

  /**
   * The security perforation is optional, so validation only kicks in once one is set.
   * Returns null when there is nothing to complain about, mirroring `validateZhihaoField`.
   */
  protected validateSecurityPerforationField(): [boolean, TranslationKey | ''] | null {
    if (!this.security_perforation || !supportsSecurityPerforation(this.perforation_type)) return null;
    const [ok, key] = validateSecurityPerforation(this.security_perforation);
    return ok ? null : [false, key as TranslationKey];
  }

  protected validateCommon(): [boolean, TranslationKey | ''] {
    if (this.face_value_value === undefined)
    {
      return [false, 'validation.faceValueNotSet'];
    }
    if (this.face_value_value === 0)
    {
      return [false, 'validation.faceValueZero'];
    }
    if (!this.face_value_unit)
    {
      return [false, 'validation.faceValueUnitNotSet'];
    }
    return this.validateSecurityPerforationField() ?? [true, ''];
  }

  validate(): [boolean, TranslationKey | ''] {
    if (this.title === "")
    {
      return [false, 'validation.titleEmpty'];
    }
    return this.validateCommon();
  }

  setPictureFromPhone(picture: ImageType): void {
    this.picture = picture;
    this.pictureDirty = true;
  }

  handleImageUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.picture = { base64: e.target?.result as string };
      this.pictureDirty = true;
    };
    reader.readAsDataURL(file);
  }
}
